import React, { useMemo, useCallback } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { VirtualScrollItem } from "@/lib/types/virtual-scroll";
import { EmptyState } from "./EmptyState";
import { TableRowsSkeleton } from "./TableRowsSkeleton";
import { hasCreatedByFilter } from "./createdByAttribution";
import { GroupHeaderRow, type GroupHeaderRowProps } from "./GroupHeaderRow";
import { MemoizedTableRow } from "./TableRow";
import { useSimpleTableContext } from "../../contexts/SimpleTableContext";
import type { BaseEntity } from "../../types";
import type { GroupHeader } from "@/lib/utils/filters/grouping-strategies/types";

/**
 * Type predicate — narrows a paginated row to a GroupHeader. The runtime list
 * holds either data rows (TData) or synthesised group-header rows; the
 * `type: "group-header"` discriminator is set when the grouping strategy
 * inserts headers.
 */
function isGroupHeaderRow(item: { id: string }): item is GroupHeader {
  return (item as { type?: string }).type === "group-header";
}
import {
  CRM_VIRTUAL_OVERSCAN,
  ESTIMATE_GROUP_HEADER_ROW_PX,
  ESTIMATE_TABLE_DATA_ROW_PX,
} from "@/lib/virtualization/crm-virtual-scroll-constants";

function useTableBodyRowMemos<TData extends BaseEntity>(
  visibleFields: Array<[string, unknown]>,
  paginatedData: TData[]
) {
  const rowStyle = useMemo(() => ({ minWidth: "max-content" }), []);

  const textareaFieldSet = useMemo(() => {
    const set = new Set<string>();
    visibleFields.forEach(([fieldId, fieldConfig]) => {
      const fc = fieldConfig as { type?: string };
      if (
        fc.type === "textarea" ||
        fieldId === "body" ||
        fieldId.includes("description") ||
        fieldId.includes("notes") ||
        fieldId.includes("content")
      ) {
        set.add(fieldId);
      }
    });
    return set;
  }, [visibleFields]);

  const dataRowIds = useMemo(() => {
    return paginatedData
      .filter((item) => (item as { type?: string }).type !== "group-header")
      .map((item) => item.id);
  }, [paginatedData]);

  const idToDataIndex = useMemo(() => {
    const m = new Map<string, number>();
    dataRowIds.forEach((id, i) => m.set(id, i));
    return m;
  }, [dataRowIds]);

  return { rowStyle, textareaFieldSet, dataRowIds, idToDataIndex };
}

function TableBodyRowsVirtualized<TData extends BaseEntity>() {
  const context = useSimpleTableContext<TData>();
  const { config, paginatedData, visibleFields, groupingContext, refs, state } = context;

  const { rowStyle, textareaFieldSet, dataRowIds, idToDataIndex } = useTableBodyRowMemos(
    visibleFields,
    paginatedData
  );

  const scrollRef = refs.tableBodyRef;

  const estimateSize = useCallback(
    (index: number) =>
      (paginatedData[index] as { type?: string })?.type === "group-header"
        ? ESTIMATE_GROUP_HEADER_ROW_PX
        : ESTIMATE_TABLE_DATA_ROW_PX,
    [paginatedData]
  );

  const virtualizer = useVirtualizer({
    count: paginatedData.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan: CRM_VIRTUAL_OVERSCAN,
    measureElement: (el: Element) => el.getBoundingClientRect().height,
  });

  const vItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <div
      style={{
        height: `${totalSize}px`,
        width: "100%",
        position: "relative",
        minWidth: "max-content",
      }}
    >
      {vItems.map((vi: VirtualScrollItem) => {
        const item = paginatedData[vi.index];
        if (!item) return null;
        const groupHeader = isGroupHeaderRow(item) ? item : null;
        const dataIndex = idToDataIndex.get(item.id) ?? 0;
        const isEditingRow = state.editingCell?.rowId === item.id;

        return (
          <div
            key={item.id}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            className="border-b border-border"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${vi.start}px)`,
              zIndex: isEditingRow ? 300 : 1,
            }}
          >
            {groupHeader ? (
              <GroupHeaderRow
                groupItem={groupHeader}
                groupingContext={groupingContext}
              />
            ) : (
              <MemoizedTableRow
                item={item}
                index={vi.index}
                textareaFieldSet={textareaFieldSet}
                rowStyle={rowStyle}
                dataRowIds={dataRowIds}
                dataIndex={dataIndex}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function TableBodyRowsComponent<TData extends BaseEntity>() {
  const context = useSimpleTableContext<TData>();

  const { config, paginatedData, data, state, visibleFields, isLoading, onCreate } = context;
  const { callbacks } = context;

  const searchTerm = state.effectiveSearchTerm;
  const advancedFilters = state.effectiveAdvancedFilters;
  const columnFilters = state.effectiveFilters;

  /** Any narrowing in effect, so the empty state can offer the right recovery. */
  const hasActiveFilters = useMemo(() => {
    const hasColumnFilter = Object.values(columnFilters ?? {}).some((value) => {
      if (value === null || value === undefined || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
    const hasAdvancedFilter = Object.keys(advancedFilters ?? {}).length > 0;
    return hasColumnFilter || hasAdvancedFilter;
  }, [columnFilters, advancedFilters]);

  const handleClearFilters = useCallback(() => {
    state.effectiveSetSearchTerm("");
    state.effectiveSetFilters({});
    callbacks.clearAllAdvancedFilters();
  }, [state, callbacks]);

  if (paginatedData.length === 0) {
    // Loading and "genuinely empty" are DIFFERENT states and used to be
    // conflated — worse, inverted. `data` is an array, so an empty result set is
    // still truthy: the old code showed "No <entity> found" while the query was
    // in flight (data === undefined) and rendered NOTHING AT ALL once an empty
    // array came back. Skeleton while loading, empty state when actually empty.
    if (isLoading) {
      return <TableRowsSkeleton columnCount={visibleFields.length} />;
    }

    return (
      <EmptyState
        entityName={config.displayName || config.entity}
        createdByFilterActive={hasCreatedByFilter(advancedFilters, columnFilters)}
        searchTerm={searchTerm}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={
          searchTerm?.trim() || hasActiveFilters ? handleClearFilters : undefined
        }
        onCreate={onCreate}
      />
    );
  }

  return <TableBodyRowsVirtualized<TData> />;
}

export const TableBodyRows = React.memo(TableBodyRowsComponent);
